import React, { memo, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import SearchAddressInput from "pages/OrderModal/components/SearchAddressInput";
import SearchBuildingInput from "pages/OrderModal/components/SearchBuildingInput";
import BaseCheckbox from "components/BaseCheckbox";
import {
  initialBox,
  possibleBoxIds,
  maxDstAddressesCount,
  sortBoxes,
} from "./constants";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { IInputBox } from "./types";
import {
  IFoundPlaces,
  IHouse,
} from "pages/OrderModal/components/SearchAddressInput/types";
import { useGetGeoDataQuery } from "store/api";
import { useAppSelector } from "store/hooks/useAppSelector";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import {
  addBox,
  clearAddress,
  clearBuilding,
  clearFoundPlaces,
  removeBox,
  reorderBoxes,
  setAddress,
  setBuilding,
  setFoundPlaces,
  setNoRoutes,
} from "store/slices/dstAddress";
import styles from "./DstAddress.styled";

const DstAddress = () => {
  const { boxes } = useAppSelector((state) => state.dstAddress);
  const { noRoutes } = useAppSelector((state) => state.dstAddress);
  const [inputStates, setInputStates] = useState<{
    [key: number]: {
      searchValue: string;
      selectedAddressValue: IFoundPlaces | null;
    };
  }>({});
  const [currentBox, setCurrentBox] = useState<IInputBox>(initialBox);
  const [currentInput, setCurrentInput] = useState<number>(0);
  const [isIconVisible, setIsIconVisible] = useState(false);
  const dispatch = useAppDispatch();
  const { data, isLoading } = useGetGeoDataQuery(
    { q: inputStates[currentInput]?.searchValue || "" },
    {
      skip:
        !inputStates[currentInput]?.searchValue ||
        inputStates[currentInput]?.searchValue.length < 3,
    },
  );

  useEffect(() => {
    setIsIconVisible(boxes.length > 1);
  }, [boxes]);

  useEffect(() => {
    if (data) {
      const foundPlacesData = [
        ...data.geo_streets.geo_street,
        ...data.geo_objects.geo_object,
      ];
      dispatch(
        setFoundPlaces({
          currentInput,
          foundPlaces: foundPlacesData,
        }),
      );
    } else {
      dispatch(clearFoundPlaces(currentInput));
    }
  }, [data]);

  useEffect(() => {
    const currentState = inputStates[currentInput];
    const selectedAddress = currentState?.selectedAddressValue;

    if (selectedAddress) {
      if (selectedAddress.houses?.length) {
        dispatch(
          setAddress({
            currentInput,
            address: selectedAddress.name,
            foundBuildings: selectedAddress.houses,
            lat: 0,
            lng: 0,
          }),
        );
      } else {
        dispatch(
          setAddress({
            currentInput,
            address: selectedAddress.name,
            foundBuildings: [],
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
          }),
        );
      }
    } else {
      dispatch(clearAddress(currentInput));
    }
  }, [inputStates, currentInput]);

  const handleDstSearch = (index: number) => (newSearchValue: string) => {
    setInputStates((prev) => ({
      ...prev,
      [index]: {
        searchValue: newSearchValue,
        selectedAddressValue: null,
      },
    }));

    setCurrentInput(index);

    if (!newSearchValue) {
      dispatch(clearFoundPlaces({ currentInput: index }));
      dispatch(clearAddress({ currentInput: index }));
      dispatch(clearBuilding({ currentInput: index }));
    }
  };

  const handleSelectedAddressValue =
    (index: number) => (value: IFoundPlaces | null) => {
      setInputStates((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          selectedAddressValue: value,
        },
      }));
      setCurrentInput(index);
    };

  const handleSelectedBuildingValue = (value: IHouse | null) => {
    if (value) {
      dispatch(
        setBuilding({
          currentInput,
          building: value.house,
          lat: value.lat,
          lng: value.lng,
        }),
      );
    }
  };

  const handleAdd = () => {
    if (boxes.length < maxDstAddressesCount) {
      const usedIds = boxes.map((box) => box.id);

      const availableId = possibleBoxIds.find((id) => !usedIds.includes(id));

      if (availableId !== undefined) {
        dispatch(
          addBox({
            id: availableId,
            order: boxes.length + 1,
            address: "",
            building: "",
            foundPlaces: [],
            foundBuildings: [],
            lat: 0,
            lng: 0,
          }),
        );
      }
    }
  };

  const handleRemove =
    (id: number) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      dispatch(removeBox({ id }));
    };

  const dragStartHandler = (
    e: React.DragEvent<HTMLDivElement>,
    box: IInputBox,
  ) => {
    setCurrentBox(box);
  };

  const dropHandler = (e: React.DragEvent<HTMLDivElement>, box: IInputBox) => {
    e.preventDefault();
    dispatch(reorderBoxes({ currentBox, box }));
  };

  const dragEndHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setNoRoutes(event.target.checked));
  };

  return (
    <>
      <Box sx={styles.checkbox}>
        <BaseCheckbox
          label="По місту"
          onChange={handleCheckboxClick}
          checked={noRoutes}
        />
      </Box>
      {!noRoutes &&
        [...boxes].sort(sortBoxes).map((box, index) => (
          <Box sx={styles.inputsWrap} key={box.id}>
            <Box
              sx={styles.dstWrap(boxes.length > 1)}
              onDragStart={(e) => dragStartHandler(e, box)}
              onDragLeave={(e) => dragEndHandler(e)}
              onDragOver={(e) => dragOverHandler(e)}
              onDragEnd={(e) => dragEndHandler(e)}
              onDrop={(e) => dropHandler(e, box)}
              draggable={boxes.length > 1}
            >
              {boxes.length > 1 && <DragIndicatorIcon sx={styles.icon} />}
              <Box sx={styles.addressWrap(boxes.length > 1)}>
                <SearchAddressInput
                  name={`dstAddress-${index}`}
                  data={box.foundPlaces || []}
                  onSearch={handleDstSearch(index)}
                  initialValue={box.address}
                  label="Куди"
                  onSelectValue={handleSelectedAddressValue(index)}
                  sx={styles.addressInput}
                  endAdornment={isIconVisible ? RemoveIcon : undefined}
                  onIconClick={handleRemove(box.id)}
                />
                {isLoading && currentInput === index && (
                  <CircularProgress
                    color="inherit"
                    size="24px"
                    sx={styles.progress(boxes.length)}
                  />
                )}
              </Box>
              <SearchBuildingInput
                name="dstBuilding"
                label="Будинок"
                data={box.foundBuildings || []}
                initialValue={box.building}
                disabled={!box.foundBuildings.length}
                onSelectValue={handleSelectedBuildingValue}
                sx={styles.buildingInput}
              />
            </Box>
            {boxes.length < 2 && (
              <Box sx={styles.addBtn} onClick={handleAdd}>
                <AddIcon />
                Додати адресу
              </Box>
            )}
            {index === 1 && boxes.length === 2 && (
              <Box sx={styles.addBtn} onClick={handleAdd}>
                <AddIcon />
                Додати адресу
              </Box>
            )}
          </Box>
        ))}
    </>
  );
};

export default memo(DstAddress);
